import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Divider,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Description as DocIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { getHelpDocs, getHelpDoc, searchHelpDocs } from '../../services/api';

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
}

interface DocFile {
  filename: string;
  title: string;
  size: number;
}

interface SearchResult {
  filename: string;
  title: string;
  matches: Array<{
    line: number;
    context: string;
  }>;
}

const HelpDialog: React.FC<HelpDialogProps> = ({ open, onClose }) => {
  const [docs, setDocs] = useState<DocFile[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const [docContent, setDocContent] = useState<string>('');
  const [docTitle, setDocTitle] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);

  // Load documentation list
  useEffect(() => {
    if (open) {
      loadDocs();
    }
  }, [open]);

  const loadDocs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getHelpDocs();
      setDocs(response.docs);
    } catch (err) {
      setError('Failed to load documentation');
      console.error('Error loading docs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDocContent = async (filename: string) => {
    try {
      setLoading(true);
      setError('');
      const response = await getHelpDoc(filename);
      setDocContent(response.content);
      setDocTitle(response.title);
      setSelectedDoc(filename);
    } catch (err) {
      setError('Failed to load documentation content');
      console.error('Error loading doc content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await searchHelpDocs(searchQuery);
      setSearchResults(response.results);
    } catch (err) {
      setError('Failed to search documentation');
      console.error('Error searching docs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderMarkdownContent = (content: string) => {
    // Simple markdown to HTML conversion for basic formatting
    // Use h4, h5, h6 to avoid conflicts with DialogTitle (h2)
    let html = content
      .replace(/^### (.*$)/gim, '<h6>$1</h6>')
      .replace(/^## (.*$)/gim, '<h5>$1</h5>')
      .replace(/^# (.*$)/gim, '<h4>$1</h4>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br>');

    return (
      <Box
        sx={{
          maxHeight: '60vh',
          overflow: 'auto',
          '& h4': { fontSize: '1.5rem', fontWeight: 'bold', mt: 2, mb: 1 },
          '& h5': { fontSize: '1.3rem', fontWeight: 'bold', mt: 2, mb: 1 },
          '& h6': { fontSize: '1.1rem', fontWeight: 'bold', mt: 1, mb: 0.5 },
          '& strong': { fontWeight: 'bold' },
          '& em': { fontStyle: 'italic' },
          '& code': { 
            backgroundColor: 'grey.100', 
            padding: '2px 4px', 
            borderRadius: '4px',
            fontFamily: 'monospace'
          },
          '& br': { lineHeight: '1.5' },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  const renderSearchResults = () => {
    if (searchResults.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No search results found.
        </Typography>
      );
    }

    return (
      <List>
        {searchResults.map((result, index) => (
          <React.Fragment key={index}>
            <ListItem>
              <ListItemButton onClick={() => loadDocContent(result.filename)}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DocIcon fontSize="small" />
                      <Typography variant="subtitle2">{result.title}</Typography>
                      <Chip label={result.matches.length} size="small" />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      {result.matches.map((match, matchIndex) => (
                        <Box key={matchIndex} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Line {match.line}:
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {match.context}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
            {index < searchResults.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5">Help Documentation</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Documentation" />
            <Tab label="Search" />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {activeTab === 0 && (
          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* Documentation List */}
            <Box sx={{ width: '300px', borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Available Documentation
                </Typography>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <List>
                  {docs.map((doc, index) => (
                    <React.Fragment key={doc.filename}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={selectedDoc === doc.filename}
                          onClick={() => loadDocContent(doc.filename)}
                        >
                          <ListItemText
                            primary={doc.title}
                            secondary={formatFileSize(doc.size)}
                          />
                          <OpenIcon fontSize="small" color="action" />
                        </ListItemButton>
                      </ListItem>
                      {index < docs.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>

            {/* Content Area */}
            <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
              {selectedDoc ? (
                <>
                  <Typography variant="h5" gutterBottom>
                    {docTitle}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    renderMarkdownContent(docContent)
                  )}
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary">
                    Select a document from the list to view its content
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={!searchQuery.trim() || loading}
              >
                Search
              </Button>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              renderSearchResults()
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HelpDialog; 